import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import OrbitLayout from '../components/OrbitLayout';
import {
  UsersThree, User, Planet, MagnifyingGlass, Eye, EyeSlash,
  Copy, Check, MapPin, Envelope, Phone, Globe, LinkSimple,
  CaretDown, CaretUp, ShieldCheck, UserCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  mercure: '#8B8B8B', venus: '#E8A838', terre: '#4A90D9',
  mars: '#D94A4A', jupiter: '#E87838', saturne: '#9B59B6',
};

const RANK_NAMES = {
  mercure: { fr: 'Mercure', en: 'Mercury' }, venus: { fr: 'Venus', en: 'Venus' },
  terre: { fr: 'Terre', en: 'Earth' }, mars: { fr: 'Mars', en: 'Mars' },
  jupiter: { fr: 'Jupiter', en: 'Jupiter' }, saturne: { fr: 'Saturne', en: 'Saturn' },
};

const SOCIAL_ICONS = {
  facebook: { label: 'Facebook', prefix: 'https://facebook.com/' },
  instagram: { label: 'Instagram', prefix: 'https://instagram.com/' },
  linkedin: { label: 'LinkedIn', prefix: 'https://linkedin.com/in/' },
  tiktok: { label: 'TikTok', prefix: 'https://tiktok.com/@' },
  youtube: { label: 'YouTube', prefix: 'https://youtube.com/@' },
  twitter: { label: 'X / Twitter', prefix: 'https://x.com/' },
  website: { label: 'Site Web', prefix: '' },
};

export default function MyTeamPage() {
  const { language } = useLanguage();
  const { user, token } = useOrbitAuth();
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepth, setFilterDepth] = useState('all');
  const [expandedMember, setExpandedMember] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const t = (fr, en) => language === 'fr' ? fr : en;
  const PAGE_SIZE = 50;

  const fetchTeam = useCallback(async (skip = 0, append = false) => {
    if (!token) return;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const res = await fetch(`${API}/api/orbit/team?limit=${PAGE_SIZE}&skip=${skip}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setTeam(prev => [...prev, ...(data.team || [])]);
        } else {
          setTeam(data.team || []);
        }
        setStats(data.stats || null);
        setHasMore(data.pagination?.has_more || false);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
    setLoadingMore(false);
  }, [token]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopiedCode(true);
      toast.success(t('Code copie !', 'Code copied!'));
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const filtered = team.filter((m) => {
    const matchSearch = !searchQuery || m.name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchDepth = filterDepth === 'all' || (filterDepth === 'direct' && m.depth === 1) || (filterDepth === 'sub' && m.depth > 1);
    return matchSearch && matchDepth;
  });

  const maxDepth = stats?.max_depth || (team.length > 0 ? Math.max(...team.map(m => m.depth)) : 0);

  return (
    <OrbitLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="my-team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="team-title">
              <UsersThree className="w-7 h-7 text-indigo-400" weight="fill" />
              {t('Mon Equipe', 'My Team')}
            </h1>
            <p className="text-gray-300 text-sm mt-1">
              {t('Gerez et suivez les membres de votre reseau', 'Manage and track your network members')}
            </p>
          </div>
          {/* Referral code share */}
          <button onClick={copyReferralCode}
            className="flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 px-4 py-2.5 rounded-xl text-sm text-indigo-300 hover:bg-indigo-600/30 transition-colors"
            data-testid="copy-referral-code">
            {copiedCode ? <Check className="w-4 h-4 text-emerald-400" weight="bold" /> : <Copy className="w-4 h-4" />}
            <span className="font-mono font-bold">{user?.referral_code || '...'}</span>
            <span className="text-indigo-400/60 text-xs">{t('Copier', 'Copy')}</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="team-stats">
            <StatCard icon={UsersThree} label={t('Total', 'Total')} value={stats.total} color="indigo" />
            <StatCard icon={User} label={t('Directs', 'Direct')} value={stats.direct} color="blue" />
            <StatCard icon={ShieldCheck} label={t('Actifs', 'Active')} value={stats.active} color="emerald" />
            <StatCard icon={Planet} label={t('Niveaux', 'Levels')} value={maxDepth} color="purple" />
          </div>
        )}

        {/* Ranks breakdown */}
        {stats && Object.keys(stats.by_rank).length > 0 && (
          <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-4" data-testid="rank-breakdown">
            <h3 className="text-sm font-semibold text-gray-400 mb-3">{t('Par rang', 'By Rank')}</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_rank).map(([rank, count]) => (
                <span key={rank}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: `${RANK_COLORS[rank] || '#555'}22`, color: RANK_COLORS[rank] || '#888' }}>
                  <Planet className="w-3.5 h-3.5" weight="fill" />
                  {RANK_NAMES[rank]?.[language] || rank} : {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('Rechercher un membre...', 'Search a member...')}
              className="w-full pl-9 pr-4 py-2.5 bg-[#1E293B] border border-indigo-500/20 rounded-xl text-white text-sm placeholder-gray-400 focus:outline-none focus:border-indigo-500"
              data-testid="team-search"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'all', fr: 'Tous', en: 'All' },
              { key: 'direct', fr: 'Directs', en: 'Direct' },
              { key: 'sub', fr: 'Sous-niveaux', en: 'Sub-levels' },
            ].map((f) => (
              <button key={f.key} onClick={() => setFilterDepth(f.key)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-colors ${
                  filterDepth === f.key ? 'bg-indigo-600 text-white' : 'bg-[#1E293B] text-gray-400 hover:text-white border border-indigo-500/20'
                }`}
                data-testid={`filter-${f.key}`}>
                {t(f.fr, f.en)}
              </button>
            ))}
          </div>
        </div>

        {/* Team Members List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-2xl p-12 text-center" data-testid="team-empty">
            <UsersThree className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-white font-bold text-lg mb-2">
              {team.length === 0
                ? t("Votre equipe est vide", "Your team is empty")
                : t("Aucun resultat", "No results")
              }
            </h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              {team.length === 0
                ? t("Partagez votre code referant pour recruter vos premiers membres.", "Share your referral code to recruit your first members.")
                : t("Aucun membre ne correspond a votre recherche.", "No members match your search.")
              }
            </p>
            {team.length === 0 && (
              <button onClick={copyReferralCode}
                className="mt-6 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-500 transition-colors"
                data-testid="share-code-cta">
                {t("Partager mon code", "Share my code")}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="team-list">
            {filtered.map((member) => (
              <MemberCard
                key={member.user_id}
                member={member}
                language={language}
                isExpanded={expandedMember === member.user_id}
                onToggle={() => setExpandedMember(expandedMember === member.user_id ? null : member.user_id)}
              />
            ))}
          </div>
        )}

        {/* Count + Load More */}
        {!loading && filtered.length > 0 && (
          <div className="text-center space-y-3">
            <p className="text-xs text-gray-500">
              {t(`${filtered.length} membre(s) affiche(s)`, `${filtered.length} member(s) shown`)}
              {stats && ` / ${stats.total} ${t('total', 'total')}`}
            </p>
            {hasMore && (
              <button onClick={() => fetchTeam(team.length, true)} disabled={loadingMore}
                className="px-6 py-2.5 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 rounded-xl text-sm font-semibold hover:bg-indigo-600/30 transition-colors disabled:opacity-50"
                data-testid="load-more-btn">
                {loadingMore
                  ? t('Chargement...', 'Loading...')
                  : t('Voir plus de membres', 'Load more members')
                }
              </button>
            )}
          </div>
        )}
      </div>
    </OrbitLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };
  return (
    <div className={`border rounded-xl p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" weight="fill" />
        <span className="text-xs font-semibold opacity-70">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function MemberCard({ member, language, isExpanded, onToggle }) {
  const t = (fr, en) => language === 'fr' ? fr : en;
  const rankColor = RANK_COLORS[member.rank] || '#8B8B8B';
  const rankName = RANK_NAMES[member.rank]?.[language] || member.rank || '?';
  const date = member.created_at ? new Date(member.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '';
  const socials = member.socials || {};
  const hasSocials = Object.values(socials).some(v => v);

  return (
    <div className={`bg-[#111827]/80 border rounded-xl overflow-hidden transition-colors ${
      member.depth === 1 ? 'border-indigo-500/30' : 'border-indigo-500/10'
    }`} data-testid={`member-card-${member.user_id}`}>
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left"
        data-testid={`member-toggle-${member.user_id}`}>
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ backgroundColor: rankColor }}>
          {member.picture
            ? <img src={member.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            : (member.name?.charAt(0)?.toUpperCase() || 'U')
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm truncate">{member.name}</span>
            {member.depth === 1 && (
              <span className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 text-[10px] font-bold rounded">
                {t('Direct', 'Direct')}
              </span>
            )}
            {!member.is_active && (
              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-bold rounded">
                {t('Inactif', 'Inactive')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {member.rank && (
              <span className="flex items-center gap-1 text-xs" style={{ color: rankColor }}>
                <Planet className="w-3 h-3" weight="fill" />
                {rankName}
              </span>
            )}
            <span className="text-gray-400 text-xs">{date}</span>
            {member.city && (
              <span className="text-gray-400 text-xs flex items-center gap-0.5">
                <MapPin className="w-3 h-3" /> {member.city}
              </span>
            )}
          </div>
        </div>

        {/* Depth indicator */}
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-[10px] text-gray-400 font-mono">
            {t('Niv.', 'Lv.')} {member.depth}
          </span>
        </div>

        {isExpanded
          ? <CaretUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <CaretDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        }
      </button>

      {/* Expanded Profile */}
      {isExpanded && (
        <div className="border-t border-indigo-500/10 p-4 bg-[#0B0E17]/50 animate-in fade-in duration-200" data-testid={`member-profile-${member.user_id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Contact */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('Contact', 'Contact')}
              </h4>
              {member.email && (
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Envelope className="w-4 h-4 text-indigo-400" /> {member.email}
                </div>
              )}
              {member.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Phone className="w-4 h-4 text-indigo-400" /> {member.phone}
                </div>
              )}
              {member.city && (
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <MapPin className="w-4 h-4 text-indigo-400" />
                  {member.city}{member.neighborhood ? ` (${member.neighborhood})` : ''}
                </div>
              )}
              {!member.email && !member.phone && !member.city && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <EyeSlash className="w-4 h-4" />
                  {t('Informations masquees par le membre', 'Information hidden by member')}
                </div>
              )}
            </div>

            {/* Social Links */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('Reseaux sociaux', 'Social Media')}
              </h4>
              {hasSocials ? (
                Object.entries(socials).filter(([, v]) => v).map(([key, value]) => {
                  const info = SOCIAL_ICONS[key] || { label: key, prefix: '' };
                  const url = value.startsWith('http') ? value : `${info.prefix}${value}`;
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-indigo-300 hover:text-indigo-200 transition-colors">
                      <LinkSimple className="w-4 h-4" />
                      {info.label}
                    </a>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400">{t('Aucun reseau social', 'No social links')}</p>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-indigo-500/10 text-xs text-gray-400">
            <span>{t('Recrues directes :', 'Direct recruits:')} <strong className="text-gray-300">{member.personally_enrolled_count || 0}</strong></span>
            <span>{t('Niveau dans votre arbre :', 'Level in your tree:')} <strong className="text-gray-300">{member.depth}</strong></span>
            {member.is_affiliate && (
              <span className="flex items-center gap-1 text-indigo-400">
                <UserCircle className="w-3.5 h-3.5" weight="fill" />
                {t('Affilie', 'Affiliate')}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
